import { Button, Divider, Modal, Radio } from 'antd';
import { useEffect, useMemo, useState } from 'react';

export type DownloadStepperOption = {
    value: string;
    label: string;
    disabled?: boolean;
};

type DownloadStepperModalProps = {
    open: boolean;
    onClose: () => void;
    onExport: (selection: { stepOneValue: string; stepTwoValue: string }) => void;
    stepOneOptions: DownloadStepperOption[];
    stepTwoOptions: DownloadStepperOption[];
    onStepOneChange?: (value: string, previousValue: string | null) => void;
    onStepAdvance?: (stepOneValue: string) => void;
};

const DownloadStepperModal = ({
    open,
    onClose,
    onExport,
    stepOneOptions,
    stepTwoOptions,
    onStepOneChange,
    onStepAdvance,
}: DownloadStepperModalProps) => {
    const defaultStepOneValue = useMemo(() => stepOneOptions[0]?.value ?? null, [stepOneOptions]);
    const [activeStep, setActiveStep] = useState<1 | 2>(1);
    const [selectedStepOneValue, setSelectedStepOneValue] = useState<string | null>(defaultStepOneValue);
    const [selectedStepTwoValue, setSelectedStepTwoValue] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setActiveStep(1);
        setSelectedStepOneValue(defaultStepOneValue);
        setSelectedStepTwoValue(null);
    }, [defaultStepOneValue, open]);

    const isStepOne = activeStep === 1;
    const isPrimaryDisabled = useMemo(
        () => (isStepOne ? !selectedStepOneValue : !selectedStepTwoValue),
        [isStepOne, selectedStepOneValue, selectedStepTwoValue],
    );

    const handlePrimaryClick = () => {
        if (isStepOne) {
            if (selectedStepOneValue) {
                onStepAdvance?.(selectedStepOneValue);
            }
            setActiveStep(2);
            return;
        }

        if (!selectedStepOneValue || !selectedStepTwoValue) return;
        onExport({ stepOneValue: selectedStepOneValue, stepTwoValue: selectedStepTwoValue });
    };

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            destroyOnClose
            centered
            width={268}
            onCancel={onClose}
            bodyStyle={{ padding: 24 }}
            modalRender={(modalNode) => <div className="overflow-hidden rounded-[6px]">{modalNode}</div>}
        >
            <div className="flex flex-col gap-4">
                <div className="flex h-6 items-center justify-between">
                    <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.99998 1.3335C8.36817 1.3335 8.66665 1.63197 8.66665 2.00016V8.39069L10.8619 6.19542C11.1223 5.93508 11.5444 5.93508 11.8047 6.19542C12.0651 6.45577 12.0651 6.87788 11.8047 7.13823L8.47139 10.4716C8.21104 10.7319 7.78893 10.7319 7.52858 10.4716L4.19524 7.13823C3.93489 6.87788 3.93489 6.45577 4.19524 6.19542C4.45559 5.93508 4.8777 5.93508 5.13805 6.19542L7.33331 8.39069V2.00016C7.33331 1.63197 7.63179 1.3335 7.99998 1.3335ZM1.99998 9.3335C2.36817 9.3335 2.66665 9.63197 2.66665 10.0002V10.8002C2.66665 11.3712 2.66717 11.7594 2.69168 12.0595C2.71556 12.3517 2.75885 12.5012 2.81197 12.6055C2.9398 12.8564 3.14378 13.0603 3.39466 13.1882C3.49893 13.2413 3.6484 13.2846 3.94067 13.3085C4.24073 13.333 4.62893 13.3335 5.19998 13.3335H10.8C11.371 13.3335 11.7592 13.333 12.0593 13.3085C12.3516 13.2846 12.501 13.2413 12.6053 13.1882C12.8562 13.0603 13.0602 12.8564 13.188 12.6055C13.2411 12.5012 13.2844 12.3517 13.3083 12.0595C13.3328 11.7594 13.3333 11.3712 13.3333 10.8002V10.0002C13.3333 9.63197 13.6318 9.3335 14 9.3335C14.3682 9.3335 14.6666 9.63197 14.6666 10.0002V10.8277C14.6667 11.3644 14.6667 11.8073 14.6372 12.168C14.6066 12.5428 14.5409 12.8872 14.376 13.2108C14.1203 13.7126 13.7124 14.1205 13.2106 14.3762C12.8871 14.541 12.5426 14.6067 12.1679 14.6374C11.8071 14.6668 11.3642 14.6668 10.8275 14.6668H5.17243C4.63579 14.6668 4.19289 14.6668 3.8321 14.6374C3.45736 14.6067 3.1129 14.541 2.78934 14.3762C2.28757 14.1205 1.87963 13.7126 1.62396 13.2108C1.4591 12.8872 1.39339 12.5428 1.36278 12.168C1.3333 11.8072 1.3333 11.3643 1.33331 10.8277L1.33331 10.0002C1.33331 9.63197 1.63179 9.3335 1.99998 9.3335Z" fill="#161227"/>
                        </svg>
                        <span className="text-[17px] leading-normal font-medium text-[#140B1D]">Export</span>
                    </div>
                </div>

                <Divider className="!my-0 !border-[#f0f0f0]" />

                <div className="flex flex-col gap-2">
                    <div className="h-8 flex items-center text-sm leading-[22px] text-black/25">
                        Choose export options
                    </div>
                    <Radio.Group
                        value={isStepOne ? selectedStepOneValue : selectedStepTwoValue}
                        onChange={(event) => {
                            const nextValue = event.target.value as string;
                            if (isStepOne) {
                                onStepOneChange?.(nextValue, selectedStepOneValue);
                                setSelectedStepOneValue(nextValue);
                                return;
                            }
                            setSelectedStepTwoValue(nextValue);
                        }}
                        className="w-full [&_.ant-radio-wrapper]:!m-0 [&_.ant-radio-wrapper]:!inline-flex [&_.ant-radio-wrapper]:!items-center [&_.ant-radio-wrapper]:!leading-[22px] [&_.ant-radio]:!top-0"
                    >
                        <div className="flex flex-col items-start gap-2">
                            {(isStepOne ? stepOneOptions : stepTwoOptions).map((option) => (
                                <Radio
                                    key={option.value}
                                    className="!m-0 !inline-flex !items-center"
                                    value={option.value}
                                    disabled={option.disabled}
                                >
                                    <span className="text-[14px] leading-[22px] text-black/88">{option.label}</span>
                                </Radio>
                            ))}
                        </div>
                    </Radio.Group>
                </div>

                <Divider className="!my-0 !border-[#f0f0f0]" />

                <div className="flex items-center justify-end gap-3">
                    <Button onClick={onClose} type="default" style={{ borderRadius: 6 }}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        disabled={isPrimaryDisabled}
                        onClick={handlePrimaryClick}
                        style={{ borderRadius: 6 }}
                    >
                        {isStepOne ? 'Next' : 'Export'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DownloadStepperModal;
